// Patrón compartido de UCI — 113/114/115 (fila 47-49 de 8.3-bis):
// "Encounter (class=ACUTE, con extensión de nivel de cuidado intensivo) +
// Observation en serie de alta frecuencia (igual patrón que 018A) +
// Condition". Extensión nueva del namespace 8.4:
// http://logiacare.ec/fhir/nivel-cuidado-intensivo (Encounter).
// Puerto de packages/hce/src/hce/forms/cuidadoIntensivo.tsx a shadcn/Tailwind:
// el Autocomplete+catálogo estático de Mantine se reemplaza por
// `SelectorDiagnosticoCie10` (ValueSet real cie10-minimo), `NumberInput` de
// Mantine por `<Input type="number">`, `onSubmitted` por `onGuardado(pares)`.
// La edición del Encounter (readReference + updateResource filtrando la
// extensión previa por url) se copia tal cual del original — patrón
// deliberado para no perder otras extensiones del Encounter.
import type { Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { SIGNOS_VITALES, VITAL_SIGNS_CATEGORY } from './vitalSigns';

// Exportada: 113 ya migró a su propio componente (`Formulario113.tsx`,
// Fase 4 Tanda 5a) y reusa esta misma extensión — 114/115 siguen acá hasta
// su propia tanda.
export const EXT_NIVEL_CUIDADO_INTENSIVO = 'http://logiacare.ec/fhir/nivel-cuidado-intensivo';

export interface CuidadoIntensivoConfig {
  readonly claveI18n: string;
  readonly nivel: string;
}

export function crearFormularioUci(config: CuidadoIntensivoConfig) {
  return function FormularioUci({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
    const { t } = useTranslation();
    const medplum = useMedplum();
    const [motivoIngreso, setMotivoIngreso] = useState<DiagnosticoCie10Valor>({ texto: '', cie10: null });
    const [valores, setValores] = useState<Record<string, number | ''>>({});
    const { guardando, error, guardar } = useGuardado();

    const handleSubmit = (): Promise<void> => guardar(async () => {
      const lote = new LoteTransaccional();
      const ahora = new Date().toISOString();
      const pares: Par[] = [];

      if (encounter) {
        const actual = await medplum.readReference(encounter);
        const otrasExtensiones = (actual.extension ?? []).filter((e) => e.url !== EXT_NIVEL_CUIDADO_INTENSIVO);
        lote.actualizarEncounterAbierto({
          ...actual,
          class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'ACUTE' },
          extension: [...otrasExtensiones, { url: EXT_NIVEL_CUIDADO_INTENSIVO, valueString: config.nivel }],
        });
      }

      if (motivoIngreso.texto.trim()) {
        lote.crear({
          resourceType: 'Condition',
          subject,
          encounter,
          code: codeDeDiagnostico(motivoIngreso),
          recordedDate: ahora,
        });
        pares.push({ pregunta: t('cuidadoIntensivo.motivoIngreso'), respuesta: conCodigoCie(motivoIngreso) });
      }

      const creaciones: Observation[] = [];
      for (const signo of SIGNOS_VITALES) {
        const valor = valores[signo.clave];
        if (valor === '' || valor === undefined) continue;
        creaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: valor, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          })
        );
        pares.push({ pregunta: signo.etiqueta, respuesta: `${valor} ${signo.unidad}` });
      }

      await lote.enviar(medplum);
      onGuardado(pares);
    });

    return (
      <div className="flex flex-col gap-4">
        <SelectorDiagnosticoCie10 label={t('cuidadoIntensivo.motivoIngreso')} value={motivoIngreso} onChange={setMotivoIngreso} />

        <h4 className="text-sm font-medium text-hce-ink">{t('cuidadoIntensivo.signosVitales')}</h4>
        {SIGNOS_VITALES.map((signo) => (
          <div key={signo.clave} className="flex flex-col gap-1">
            <Label htmlFor={`uci-signo-${signo.clave}`}>{`${signo.etiqueta} (${signo.unidad})`}</Label>
            <Input
              id={`uci-signo-${signo.clave}`}
              type="number"
              step="0.1"
              value={valores[signo.clave] ?? ''}
              onChange={(e) => setValores((prev) => ({ ...prev, [signo.clave]: e.target.value === '' ? '' : Number(e.target.value) }))}
            />
          </div>
        ))}

        <ErrorGuardado error={error} />

        <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
          {t('formulario.guardar')}
        </Button>
      </div>
    );
  };
}
